/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { Backdrop, FormControlLabel, Radio, RadioGroup, Tab, Tabs } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Theme, createStyles, makeStyles, withStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../utils/api";
import { getActivePlans, getActiveVEBPlans } from "../../../../../../utils/api/plan/plan";
import { Plan, VEB_Plan } from "../../../../../../@types/plan.types";
import { zipToCoordinateConverter } from "../../../../../../utils/commonFunctions/zipToCoordinateConverter";
import { CustomMapView } from "../../../../../shared";
import ScrollTabPanel from "../../../../../shared/tabPanelComponent/ScrollTabPanel";
import NoEnrollmentFoundCard from "./noEnrollmentFoundCard/NoEnrollmentFoundCard";
import a11yScrollableProps from "../../../../../../constants/tabPanelProps/ayScrollableProps";
import { MagnifyingGlass } from "react-loader-spinner";
import {
	CoreMarkers,
	EnrollmentMapSectionProps,
	VEBMapMarkers
} from "../../../../../../@types/components/enrollmentMapSection.types";

import "./enrollmentMapSection.css";

const GreenSwitch = withStyles({
	switchBase: {
		color: "white",
		"&$checked": {
			color: "white"
		},
		"&$checked + $track": {
			backgroundColor: "white"
		}
	},
	checked: {},
	track: {
		backgroundColor: "white"
	}
})(Switch);

const GreenRadio = withStyles({
	root: {
		color: "#FFBF00",
		"&$checked": {
			color: "#FFBF00"
		}
	},
	checked: {}
})((props: any) => <Radio color="default" {...props} />);

const tabStyles = makeStyles({
	customTab: {
		minWidth: "180px !important" // Adjust the width as needed
	}
});

const backdropStyles = makeStyles((theme: Theme) =>
	createStyles({
		backdrop: {
			zIndex: theme.zIndex.drawer + 1,
			color: "#fff",
			position: "absolute"
		}
	})
);

const sortByPlanOrder = <T,>(plans: T[]): T[] =>
	[...plans].sort((a: any, b: any) => (a.plan_order ?? 0) - (b.plan_order ?? 0));

// Only the first plan for a given plan_name — the backend can return more than one document per name
// (e.g. across plan years), but a tab set should show each plan name once.
// NOTE: deliberately NOT filtering by plan_year === current calendar year here. plan_year on real data
// doesn't reliably match the calendar year (the backend's own "current year" plan-code lookups have that
// filter commented out — see node_server_typescript plan.service.ts / plan.controllers.ts), so filtering
// on it client-side was zeroing out legitimately active plans.
const dedupeByPlanName = <T,>(plans: T[]): T[] => {
	const seenPlanNames = new Set<string>();
	return plans.filter((plan: any) => {
		if (seenPlanNames.has(plan.plan_name)) return false;
		seenPlanNames.add(plan.plan_name);
		return true;
	});
};

const EnrollmentMapSectionV2: React.FC<EnrollmentMapSectionProps> = ({ refresh, setRefresh }) => {
	const [planCheckedStatus, setPlanCheckedStatus] = useState(false);
	// Tabs are no longer a hardcoded list — they're whatever plans the backend says are currently active
	// for this group. Adding/removing/renaming a plan on the backend is reflected here automatically.
	const [corePlans, setCorePlans] = useState<Plan[]>([]);
	const [vebPlans, setVebPlans] = useState<VEB_Plan[]>([]);
	const [plansLoaded, setPlansLoaded] = useState(false);
	const [selectedCorePlan, setSelectedCorePlan] = useState<string>("");
	const [selectedVEBPlan, setSelectedVEBPlan] = useState<string>("");
	const [_coreMarkers, _setCoreMarkers] = useState<CoreMarkers[]>([]);
	const [_VEBMapMarks, _setVEBMapMarkers] = useState<VEBMapMarkers[]>([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [mapType, setMapType] = useState<string>("ZIP WISE");
	const [loadingState, setLoadingState] = useState<boolean>(false);

	const queryGroupNumber = useRouteParam("gn");
	const tabStyleClass = tabStyles();
	const backdropClasses = backdropStyles();

	// ======================================================================================================
	// Fetch the group's currently active Core + VEB plans and use them as the tab source.
	// ======================================================================================================
	useEffect(() => {
		if (!queryGroupNumber) return;

		const fetchActivePlans = async () => {
			setLoadingState(true);
			setTabIndex(0);
			// Clear out the previous group's plans immediately — otherwise, during the fetch below, this
			// component keeps rendering the old group's tabs/map (or wrongly infers "no VEB plans") off
			// stale state instead of waiting for the new group's real result.
			setPlansLoaded(false);
			setCorePlans([]);
			setVebPlans([]);
			try {
				const [corePlanList, vebPlanResponse] = await Promise.all([
					getActivePlans(queryGroupNumber as number),
					getActiveVEBPlans(queryGroupNumber as number)
				]);

				const _corePlans = dedupeByPlanName(sortByPlanOrder(corePlanList || []));
				setCorePlans(_corePlans);
				setSelectedCorePlan(_corePlans[0]?.plan_name ?? "");

				const _vebPlans = dedupeByPlanName(vebPlanResponse?.currentPlans ?? []);
				setVebPlans(_vebPlans);
				setSelectedVEBPlan(_vebPlans[0]?.plan_name ?? "");
			} catch (error) {
				console.error("Failed to fetch active plans for map projection", error);
			} finally {
				setPlansLoaded(true);
			}
		};

		fetchActivePlans();
	}, [queryGroupNumber]);

	const handleCoreEnrollmentMapProjectionClick = useCallback(
		(_event: any, index: number) => {
			const plan = corePlans[index];
			if (!plan) return;
			setTabIndex(index);
			setSelectedCorePlan(plan.plan_name);
		},
		[corePlans]
	);

	const handleVEBEnrollmentMapProjectionClick = useCallback(
		(_event: any, index: number) => {
			const plan = vebPlans[index];
			if (!plan) return;
			setTabIndex(index);
			setSelectedVEBPlan(plan.plan_name);
		},
		[vebPlans]
	);

	const getInitActiveMemberProjectionDetails = useCallback(
		async (queryGroupNumber: number, enrollmentType: string, selectedPlan: string, mapType: string) => {
			const projectionPayload = {
				group_number: queryGroupNumber,
				enrollment_type: enrollmentType,
				plan_name: selectedPlan,
				map_type: mapType
			};

			// api-call
			const response = await api.benefitSummary.getBenefitMemberProjection(projectionPayload);
			const { activeBenefitMemberProjectionInstance }: { activeBenefitMemberProjectionInstance: any[] } =
				response || [];

			if (response)
				setTimeout(() => {
					setLoadingState(false);
				}, 1500);

			const buildLocationCoordinates = (instance: any[]) =>
				instance.map(({ member_count, employee_name, ZIP }) => ({
					positionProps: zipToCoordinateConverter(ZIP),
					nameProps: mapType === "MEMBER WISE" ? employee_name : member_count
				}));

			// ======================================================= (CORE ENROLLMENT) ==========================
			const _storedCoreMarkersParent: CoreMarkers[] = Array.from({ length: corePlans.length }, () => []);

			if (enrollmentType === "CORE" && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
				const planIndex = corePlans.findIndex(({ plan_name }) => plan_name === selectedPlan);
				if (planIndex !== -1) {
					_storedCoreMarkersParent[planIndex] = buildLocationCoordinates(
						activeBenefitMemberProjectionInstance
					);
				}
			}

			// ======================================================= (VEB ENROLLMENT) ==========================
			const _storedVEBMarkersParent: VEBMapMarkers[] = Array.from({ length: vebPlans.length }, () => []);

			if (enrollmentType === "VEB" && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
				const planIndex = vebPlans.findIndex(({ plan_name }) => plan_name === selectedPlan);
				if (planIndex !== -1) {
					_storedVEBMarkersParent[planIndex] = buildLocationCoordinates(
						activeBenefitMemberProjectionInstance
					);
				}
			}

			// CORE-ENROLLMENTS
			_setCoreMarkers(_storedCoreMarkersParent);
			// VEB-ENROLLMENTS
			_setVEBMapMarkers(_storedVEBMarkersParent);
		},
		[corePlans, vebPlans]
	);

	const handlePlanSwitchClick = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const { checked } = event.target;
			setPlanCheckedStatus(checked);
			setTabIndex(0);
			setLoadingState(true);

			if (checked === true) {
				const firstVEBPlan = vebPlans[0]?.plan_name ?? "";
				setSelectedVEBPlan(firstVEBPlan);
				if (firstVEBPlan && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
					await getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						"VEB",
						firstVEBPlan,
						mapType
					);
				}
			}

			if (checked === false) {
				const firstCorePlan = corePlans[0]?.plan_name ?? "";
				setSelectedCorePlan(firstCorePlan);
				if (firstCorePlan && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
					await getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						"CORE",
						firstCorePlan,
						mapType
					);
				}
			}
		},
		[corePlans, vebPlans, getInitActiveMemberProjectionDetails, mapType, queryGroupNumber]
	);

	const handleMapTypeChangeClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		const _mapTypeToSet = event.target.value;
		setMapType(_mapTypeToSet);
	};

	useEffect(() => {
		if (!plansLoaded) return;

		const enrollmentType = planCheckedStatus ? "VEB" : "CORE";
		const selectedPlan = enrollmentType === "CORE" ? selectedCorePlan : selectedVEBPlan;
		if (!selectedPlan) return;

		setLoadingState(true);

		if (queryGroupNumber && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
			getInitActiveMemberProjectionDetails(queryGroupNumber as number, enrollmentType, selectedPlan, mapType);
		}

		if (refresh) {
			if (queryGroupNumber && (mapType === "ZIP WISE" || mapType === "MEMBER WISE")) {
				getInitActiveMemberProjectionDetails(queryGroupNumber as number, enrollmentType, selectedPlan, mapType);
			}
			setRefresh(false);
		}
	}, [
		getInitActiveMemberProjectionDetails,
		mapType,
		plansLoaded,
		planCheckedStatus,
		queryGroupNumber,
		refresh,
		selectedCorePlan,
		selectedVEBPlan,
		setRefresh
	]);

	return (
		<div className="enrollment-map-section" id="enrollment-map-section">
			<div className="enrollment-map-section-container">
				<div className="map-section-container">
					<div className="specific-enrollment-map-header-container" id="specific-enrollment-container">
						<div className="header-group">
							<h1 className="specific-enrollment-header-text">
								{planCheckedStatus ? "Member Veb Benefit Projection" : "Member Core Benefit Projection"}
							</h1>
							<RadioGroup
								className="radio-button-group"
								aria-label="mapType"
								value={mapType}
								onChange={handleMapTypeChangeClick}
							>
								<FormControlLabel value="ZIP WISE" control={<GreenRadio />} label="ZIP Wise" />
								<FormControlLabel value="MEMBER WISE" control={<GreenRadio />} label="Member Wise" />
							</RadioGroup>
						</div>

						<div className="switch-container" id="switch-container">
							<Grid className="switch-grid-container" container spacing={1}>
								<Grid className="core-plan-switch-container" item>
									Core Plans
								</Grid>
								<Grid item>
									<GreenSwitch
										checked={planCheckedStatus}
										onChange={handlePlanSwitchClick}
										className="plan-switch-custom"
									/>
								</Grid>
								<Grid className="veb-plan-switch-container" item>
									VEB Plans
								</Grid>
							</Grid>
						</div>
					</div>
					<div className="specific-enrollment-map-container-main" id="specific-enrollment-map-container-main">
						{!planCheckedStatus ? (
							!plansLoaded ? (
								<div className="map-section-main-container" id="map-section-main-container" />
							) : corePlans.length === 0 ? (
								<NoEnrollmentFoundCard title="No Core Plans Found" />
							) : (
								// ============================= CORE ENROLLMENT ======================================
								<div className="map-section-main-container" id="map-section-main-container">
									<div className="tab-container">
										<Tabs
											// orientation="vertical"
											variant="scrollable"
											value={tabIndex}
											onChange={handleCoreEnrollmentMapProjectionClick}
											style={{
												minWidth: "10rem",
												boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
												// borderRadius: "0 0 10px 10px",
												// backgroundColor: "#CBE4DE",
												backgroundColor: "#b9b5f2",
												color: "#2E4F4F"
											}}
											aria-label="Vertical tabs example"
											className="glb-tab-panel-custom"
											TabIndicatorProps={{
												style: {
													border: "none",
													backgroundColor: "#6a63d4"
												}
											}}
										>
											{corePlans.map((plan, index) => (
												<Tab
													key={plan._id ?? index}
													label={plan.plan_name}
													className={`glb-tab-label ${tabStyleClass.customTab}`}
													{...a11yScrollableProps(index)}
												/>
											))}
										</Tabs>
									</div>
									<div className="map-container">
										{_coreMarkers &&
											_coreMarkers.map((marker, index) => {
												return (
													<ScrollTabPanel key={index} value={tabIndex} index={index}>
														<div className="tab-section-information-container">
															<CustomMapView
																mapType={mapType}
																markers={marker}
																index={index} // Pass index to CustomMapView
															/>
														</div>
													</ScrollTabPanel>
												);
											})}
										<Backdrop className={backdropClasses.backdrop} open={loadingState}>
											<MagnifyingGlass
												visible={true}
												height="80"
												width="80"
												ariaLabel="MagnifyingGlass-loading"
												wrapperStyle={{}}
												wrapperClass="MagnifyingGlass-wrapper"
												glassColor="#ffe7a6"
												color="#8b97f3"
											/>
										</Backdrop>
									</div>
								</div>
							)
						) : !plansLoaded ? (
							<div className="map-section-main-container" id="map-section-main-container" />
						) : vebPlans.length === 0 ? (
							<NoEnrollmentFoundCard />
						) : (
							// ============================= VEB ENROLLMENT ======================================
							<div className="map-section-main-container" id="map-section-main-container">
								<div className="tab-container">
									<Tabs
										// orientation="vertical"
										variant="scrollable"
										value={tabIndex}
										onChange={handleVEBEnrollmentMapProjectionClick}
										style={{
											minWidth: "10rem",
											boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
											// borderRadius: "0 0 10px 10px",
											backgroundColor: "#b9b5f2",
											color: "#2E4F4F"
										}}
										aria-label="Vertical tabs example"
										className="glb-tab-panel-custom"
										TabIndicatorProps={{
											style: {
												border: "none",
												backgroundColor: "#6a63d4"
											}
										}}
									>
										{vebPlans.map((plan, index) => (
											<Tab
												key={plan._id ?? index}
												label={plan.plan_name}
												className={`glb-tab-label ${tabStyleClass.customTab}`}
												{...a11yScrollableProps(index)}
											/>
										))}
									</Tabs>
								</div>
								<div className="map-container">
									{_VEBMapMarks &&
										_VEBMapMarks.map((marker, index) => {
											return (
												<ScrollTabPanel key={index} value={tabIndex} index={index}>
													<div className="tab-section-information-container">
														<CustomMapView
															mapType={mapType}
															markers={marker}
															index={index} // Pass index to CustomMapView
														/>
													</div>
												</ScrollTabPanel>
											);
										})}
									<Backdrop className={backdropClasses.backdrop} open={loadingState}>
										<MagnifyingGlass
											visible={true}
											height="80"
											width="80"
											ariaLabel="MagnifyingGlass-loading"
											wrapperStyle={{}}
											wrapperClass="MagnifyingGlass-wrapper"
											glassColor="#ffe7a6"
											color="#8b97f3"
										/>
									</Backdrop>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default EnrollmentMapSectionV2;
